"use client";

import { createContext, useContext, useEffect, type ReactNode } from "react";
import { reportBrowserError } from "./reporter";
import "./error-fallback.css";

// The page-level error boundary's content (app/[locale]/error.tsx): a calm
// message in the page's language, a retry and a support reference, and a
// report of the failure. The root layout provides the copy, because an error
// boundary is a client component without the server's catalogs.

export type ErrorCopy = {
  title: string;
  text: string;
  retry: string;
  home: string;
  homeHref: string;
  /** Contains {reference}. */
  reference: string;
};

const ErrorCopyContext = createContext<ErrorCopy | null>(null);

/** Rendered once by the root layout around the page; also loads the browser error capture. */
export function BrowserErrorCapture({ copy, children }: { copy: ErrorCopy; children: ReactNode }) {
  return <ErrorCopyContext.Provider value={copy}>{children}</ErrorCopyContext.Provider>;
}

export type ErrorFallbackProps = {
  error: Error & { digest?: string };
  reset: () => void;
};

export function ErrorFallback({ error, reset }: ErrorFallbackProps) {
  const copy = useContext(ErrorCopyContext);
  useEffect(() => {
    reportBrowserError({ kind: "boundary", type: error.name, message: error.message, stack: error.stack, digest: error.digest });
  }, [error]);
  // Without the layout's copy there is no language to speak; the boundary above handles it.
  if (!copy) throw error;
  const reference = error.digest ? copy.reference.replace("{reference}", error.digest.slice(0, 12)) : null;
  return (
    <main className="iron-error" role="alert">
      <h1>{copy.title}</h1>
      <p>{copy.text}</p>
      <div className="iron-error-actions">
        <button type="button" className="btn btn-primary" onClick={reset}>{copy.retry}</button>
        <a className="btn btn-ghost" href={copy.homeHref}>{copy.home}</a>
      </div>
      {reference ? <p className="iron-error-reference">{reference}</p> : null}
    </main>
  );
}
